import React, { useState } from 'react'
import { NavLink, useHistory } from 'react-router-dom'
import Icon from 'components/Icon'
import classNames from 'classnames'
import { useKeyboardShortcut } from '@arnsbogroup/react-voicearchive/lib/hooks'
import { Kbd, Chip } from '@arnsbogroup/react-voicearchive'
import useCurrentUser from 'hooks/useCurrentUser'

function withKbdShortcut(props, Component) {
  const { route } = props
  const { path, href, kbd, onClick, disabled, routes } = route
  const history = useHistory()
  const { track } = useCurrentUser()

  const onKbd = (keys, localLocation, externalLocation) => {
    if (localLocation) {
      return window.open(localLocation, '_blank')
    }

    if (onClick) {
      return onClick()
    }

    history.push(externalLocation)
  }

  const registerShortCutFn = (kbd, eventHandler) => {
    if (kbd) {
      useKeyboardShortcut(kbd, eventHandler, {
        afterCallback: (keys) => track.increment('navigation', { path, href, kbd: Object.keys(keys).join('+') })
      })

      useKeyboardShortcut(['Ctrl', ...kbd], eventHandler, {
        afterCallback: (keys) => track.increment('navigation', { path, href, kbd: Object.keys(keys).join('+') })
      })
    }
  }

  registerShortCutFn(kbd, (keys) => {
    onKbd(keys, href, path)
  })

  if (routes) {
    routes.forEach(route => registerShortCutFn(route.kbd, (e) => {
      onKbd(e, route.href, route.path)
    }))
  }

  return (
    <Component {...props } />
  )
}

const Badge = ({ type }) => (
  <Chip
    size="xs"
    state={{
      NEW: 'success',
      EXPERIMENTAL: 'warning',
      DEPRECATED: 'error',
    }[type] ?? 'info'}
    children={{
      NEW: 'New',
      EXPERIMENTAL: 'Beta',
      DEPRECATED: 'Deprecated',
    }[type] ?? 'N/A'}
  />
)

const NavItem = ({ showLabel = true, route }) => {
  const { path = '', icon, name, cta = false, disabled = false, onClick, href, routes = [], kbd, badge } = route
  const [showRoutes, setShowRoutes] = useState(false)
  const { track } = useCurrentUser()

  const onBeforeClick = (event) => {
    event.persist()
    track.increment('navigation', { path, href })

    if (href || disabled) {
      event.stopPropagation()
      event.preventDefault()
    }

    if (href) {
      return window.open(href, '_blank')
    }

    if (onClick) {
      onClick(event)
    }
  }

  const onNavItemClick = () => {
    if (!!routes.length) {
      setShowRoutes(!showRoutes)
    }
  }

  return (
    <NavLink
      title={React.isValidElement(name) ? path : name}
      exact={path == '/'}
      to={path}
      className={
        disabled ? `block border-2 border-gray-50 text-gray-300 text-sm font-medium rounded-sm cursor-not-allowed` : cta ?
          `block border-2 border-primary text-primary hover:bg-primary hover:text-white group text-sm font-medium rounded-sm` :
          `block border-2 border-gray-50 text-gray-600 hover:bg-gray-100 group text-sm font-medium rounded-sm`
      }
      activeClassName="bg-gray-200 border-gray-200"
      onClick={onBeforeClick}
    >
      <div className={classNames('p-2 flex items-center', { 'justify-center': !showLabel })} onClick={onNavItemClick}>
        <Icon
          icon={['fal', icon]}
          className={classNames('font-bold text-lg fa-fw', {
            'text-gray-400': !showRoutes && !cta,
            'text-primary-ligher':  showRoutes,
          })}
        />

        { showLabel &&
          <div className="grow flex justify-between items-center ml-3">
              <div>
                <div>{ name }</div>
              </div>
              <div className="space-x-1">
                { badge ? (
                  <Badge type={badge} />
                ) : kbd ? (
                  <Kbd size="xxs">{ kbd.join(' + ') }</Kbd>
                ) : null }
                
                { !!routes.length &&
                  <Icon className="font-bold text-gray-400 text-xs fa-fw" icon={['fal', showRoutes ? 'chevron-up' : 'chevron-down']} />
                }
              </div>
          </div>
        }
      </div>

      { showRoutes && !!routes.length && routes.map((route) => (
        <SubNavItem
          key={route.path}
          route={route}
          showLabel={showLabel}
          parentName={name}
        />
      )) }
    </NavLink>
  )
}

const SubNavItem = ({ parentName, showLabel, route }) => {
  const { track } = useCurrentUser()

  return (
  <NavLink
    title={`${parentName} > ${route.name}`}
    exact={route.path == '/'}
    to={route.path}
    key={route.path}
    className={classNames('block py-3 hover:text-primary', {
      'px-2': showLabel,
      'text-center': !showLabel,
    })}
    activeClassName="bg-gray-200"
    onClick={(event) => {
      const { onClick, href, disabled, path } = route

      event.persist()
      track.increment('navigation', { path, href })

      if (onClick || href || disabled) {
        event.stopPropagation()
        event.preventDefault()
      }

      if (href) {
        return window.open(href, '_blank')
      }

      if (onClick) {
        return onClick(event)
      }
    }}
  >
    <div className={classNames('px-2 flex items-center', { 'justify-center': !showLabel })}>
      <Icon className={classNames('font-bold text-sm pr-6 fa-fw', { 'pl-2': showLabel })} icon={['fal', route.icon]} />

      { showLabel &&
        <div className="grow flex justify-between items-center ml-3">
          <span>{ route.name }</span>
          <div className="space-x-1">
            { route.badge ? (
              <Badge type={route.badge} />
            ) : route.kbd ? (
              <Kbd size="xxs">{ route.kbd.join(' + ') }</Kbd>
            ) : null }
          </div>
        </div>
      }
    </div>
  </NavLink>
)
}

export default (props) => {
  return withKbdShortcut(props, NavItem)
}
